import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";

const APIURL = process.env.REACT_APP_BASE_API_URL;
const API_URL = `${APIURL}/api/modules`;

interface Module {
  id: string;
  name: string;
  description: string;
  prefix: string;
  sequence: number;
  createdDate?: string;
  updatedDate?: string;
}

interface EditModuleProps {
  module: Module | null;
  handleClose?: () => void;
  onSubmit?: () => void;
  fetchModulesData?: () => void;
}

const EditModule: React.FC<EditModuleProps> = ({
  module,
  handleClose,
  onSubmit,
  fetchModulesData,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    prefix: "",
    sequence: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ Prefill form data when module changes
  useEffect(() => {
    if (module) {
      setFormData({
        id: module.id,
        name: module.name,
        description: module.description || "",
        prefix: module.prefix,
        sequence: module.sequence.toString(),
      });
    }
  }, [module]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...formData,
        sequence: parseInt(formData.sequence) || 0,
      };

      const response = await axios.put(API_URL, payload);

      if (response.data) {
        setSuccess("✅ Module updated successfully!");
        if (fetchModulesData) fetchModulesData();
        if (onSubmit) onSubmit();
      }
    } catch (err: any) {
      console.error("Error updating module:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to update module";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (handleClose) handleClose();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Typography variant="h5" gutterBottom>
        ✏️ Edit Module
      </Typography>

      <form onSubmit={handleSubmit}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Module Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="prefix"
              label="Prefix"
              value={formData.prefix}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
              helperText="Unique identifier for the module"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="sequence"
              label="Sequence"
              type="number"
              value={formData.sequence}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
              helperText="Display order sequence"
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
          >
            <Button onClick={handleCancel} disabled={loading} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Update Module"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default EditModule;
